import cx from 'classnames';
import {
  type FC,
  lazy,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {capitalizeFirstLetter} from '~/common/utils';
import {FiltersButton} from '~/GalleryPB/components/FilterManager/FiltersButton';
import {ResultsCount} from '~/GalleryPB/components/ResultsCount';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import {selectSortBy} from '~/GalleryPB/reducers/filters';
import {SortByOption} from '~/GalleryPB/types/filters';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';
import {Dropdown} from '~library/molecules/Dropdown';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';

import * as styles from './ResultsHeader.module.scss';

const PreviewPhotoUpload = lazy(() =>
  import('~/GalleryPB/components/PreviewPhotoUpload/PreviewPhotoUpload').then((module) => ({
    default: module.PreviewPhotoUpload,
  }))
);

interface ResultsHeaderProps {
  showFilterButton?: boolean;
  onFilterButtonClick: MouseEventHandler<HTMLButtonElement>;
  onSortByChange: (sortBy: SortByOption) => void;
  numTemplates?: number;
  areFiltersOpen?: boolean;
}

export const ResultsHeader: FC<ResultsHeaderProps> = ({
  showFilterButton,
  onFilterButtonClick,
  onSortByChange,
  numTemplates,
  areFiltersOpen = false,
}) => {
  const isLargeAndUp = useIsLargeAndUp();
  const sortBy = useSelector(selectSortBy);
  const [isSortByDropdownOpen, setIsSortByDropdownOpen] = useState(false);
  const {isInUyoOrDyo} = useUyoOrDyo();

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to close the dropdown when we switch from mobile to desktop
  useEffect(() => {
    setIsSortByDropdownOpen(false);
  }, [isLargeAndUp]);

  const SortByTrigger = useMemo(
    () => (
      <div className={styles.sortByTrigger}>
        <Typography variant="paragraph2">
          {capitalizeFirstLetter(sortBy.replaceAll('_', ' '))}
        </Typography>
        <ChevronIcon ratio={0.75} direction={isSortByDropdownOpen ? 'up' : 'down'} />
      </div>
    ),
    [sortBy, isSortByDropdownOpen]
  );

  const handleSortByChange = useCallback(
    (value: SortByOption) => {
      onSortByChange(value);
      setIsSortByDropdownOpen(false);
    },
    [onSortByChange]
  );

  const SortByDropdownContent = useMemo(
    () => (
      <div className={styles.sortByDropdownContent}>
        {[SortByOption.MOST_POPULAR, SortByOption.NEWLY_ADDED].map((sortOption) => (
          <Button
            key={sortOption}
            variant="secondary"
            size="medium"
            data-qa-id={`gallery__sort-by-option__${sortOption}`}
            className={styles.sortByDropdownOption}
            onClick={() => handleSortByChange(sortOption)}
          >
            {capitalizeFirstLetter(sortOption.replaceAll('_', ' '))}
            {sortBy === sortOption ? <CheckmarkIcon ratio={0.75} /> : null}
          </Button>
        ))}
      </div>
    ),
    [sortBy, handleSortByChange]
  );

  return (
    <div className={styles.resultsHeader} id="gallery-results-header">
      <div className={cx(styles.numTemplates, {[styles.uyoDyoOn]: isInUyoOrDyo})}>
        {numTemplates !== undefined ? <ResultsCount numTemplates={numTemplates} /> : null}
      </div>
      <div className={styles.actions}>
        {isLargeAndUp && (
          <div className={styles.sortByContainer}>
            <Typography variant="paragraph2" color="pebble">
              Sort by:
            </Typography>
            <Dropdown
              open={isSortByDropdownOpen}
              setOpen={setIsSortByDropdownOpen}
              trigger={SortByTrigger}
              buttonVariant="secondary"
              buttonSize="medium"
              content={SortByDropdownContent}
              dropdownClassName={styles.sortByDropdown}
            />
          </div>
        )}
        {showFilterButton && (
          <FiltersButton onClick={onFilterButtonClick} areFiltersOpen={areFiltersOpen} />
        )}
      </div>
      {isInUyoOrDyo ? (
        <div className={styles.upload}>
          <PreviewPhotoUpload />
        </div>
      ) : null}
    </div>
  );
};
